import { useState } from "react";

import {
  CONSUMABLES,
  provisioningPlan,
} from "@nav-games/predeparture";

/// Provisioning calculator — input crew count + passage duration +
/// level, output total + per-day + reserve for every consumable.
/// Used in the Reference Predeparture section.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function Field({
  label,
  value,
  onChange,
  step = 1,
  type = "number",
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step?: number;
  type?: string;
  hint?: string;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        flex: 1,
        minWidth: 120,
      }}
    >
      <span className="small muted">{label}</span>
      <input
        type={type}
        inputMode={type === "number" ? "decimal" : "text"}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
      {hint && (
        <span className="small muted" style={{ fontSize: 11 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function ProvisioningCalculator() {
  const [crew, setCrew] = useState("4");
  const [days, setDays] = useState("7");
  const [level, setLevel] = useState<"minimum" | "rated" | "generous">("rated");
  const [reservePct, setReservePct] = useState("30");

  const crewN = Number(crew);
  const daysN = Number(days);
  const reserveN = Number(reservePct);
  const valid =
    Number.isFinite(crewN) &&
    crewN > 0 &&
    Number.isFinite(daysN) &&
    daysN > 0 &&
    Number.isFinite(reserveN) &&
    reserveN >= 0;

  const plan = valid
    ? provisioningPlan({
        crewCount: crewN,
        days: daysN,
        level,
        reserveFraction: reserveN / 100,
      })
    : [];

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Provisioning calculator</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Plan water + fuel + food + gas + battery for a passage. Pick
        the level — minimum (emergency only), rated (typical comfort),
        or generous (long ocean leg). The reserve adds on top of the
        base figure to cover delays + weather.
      </p>

      <div
        style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}
      >
        <Field
          label="Crew (people)"
          value={crew}
          onChange={setCrew}
          step={1}
        />
        <Field
          label="Passage (days)"
          value={days}
          onChange={setDays}
          step={1}
        />
        <Field
          label="Reserve (%)"
          value={reservePct}
          onChange={setReservePct}
          step={5}
          hint="RYA-suggested 30%"
        />
      </div>

      <div
        style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}
      >
        {(["minimum", "rated", "generous"] as const).map((l) => (
          <button
            key={l}
            type="button"
            className={level === l ? "" : "secondary"}
            onClick={() => setLevel(l)}
            style={{ flex: "1 1 100px", textTransform: "capitalize" }}
            aria-pressed={level === l}
          >
            {l}
          </button>
        ))}
      </div>

      {valid && (
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                textAlign: "left",
                color: "var(--text-muted)",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <th style={{ padding: "6px 8px" }}>Consumable</th>
              <th style={{ padding: "6px 8px" }}>Per day</th>
              <th style={{ padding: "6px 8px" }}>Total</th>
              <th style={{ padding: "6px 8px" }}>+ Reserve</th>
              <th style={{ padding: "6px 8px" }}>Final</th>
            </tr>
          </thead>
          <tbody>
            {plan.map((p) => {
              const c = CONSUMABLES.find((x) => x.id === p.consumable)!;
              const final = p.totalQuantity + p.reserveQuantity;
              const isFuel =
                p.consumable === "fuel-cruise" || p.consumable === "fuel-charge";
              // For fuel, multiply per-day by hours/day assumed default (we
              // can't know hours/day so just show per engine-hour total).
              return (
                <tr
                  key={p.consumable}
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <td style={{ padding: "8px" }}>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div className="small muted" style={{ marginTop: 2 }}>
                      {c.unit}
                    </div>
                  </td>
                  <td style={{ padding: "8px" }}>
                    {isFuel
                      ? `${p.perDay.toFixed(1)} /hr`
                      : p.perDay.toFixed(1)}
                  </td>
                  <td style={{ padding: "8px" }}>
                    {p.totalQuantity.toFixed(1)}
                  </td>
                  <td style={{ padding: "8px" }} className="muted">
                    +{p.reserveQuantity.toFixed(1)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      fontWeight: 700,
                      color: "var(--accent)",
                    }}
                  >
                    {final.toFixed(1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!valid && (
        <p className="small muted" style={{ margin: 0 }}>
          Enter valid crew, days + reserve values.
        </p>
      )}

      <p className="small muted" style={{ marginTop: 12, marginBottom: 0 }}>
        <strong>Fuel note:</strong> the table shows per-engine-hour
        burn. Estimate your engine hours separately based on
        passage-planning assumptions (e.g. 3 h/day on a Solent cruise,
        zero on a fast ocean leg).
      </p>
    </div>
  );
}
